import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Phone,
  MapPin,
  Building2,
} from "lucide-react";

/* ================= CONTACT PAGE ================= */
export default function Contact() {
  const [dark, setDark] = useState(true);

  /* ================= THEME SYNC ================= */
  useEffect(() => {
    const loadTheme = () => {
      const t = localStorage.getItem("theme");
      setDark(t !== "light");
    };

    loadTheme();
    window.addEventListener("theme-change", loadTheme);
    return () => window.removeEventListener("theme-change", loadTheme);
  }, []);

  const theme = {
    page: dark
      ? "bg-[#0a0a0a] text-white"
      : "bg-gray-100 text-gray-900",

    card: dark
      ? "bg-white/5 border-white/10"
      : "bg-white border-gray-200",

    muted: dark ? "text-gray-400" : "text-gray-500",

    icon: dark ? "text-sky-400" : "text-sky-600",
  };

  return (
    <div className={`min-h-screen py-20 px-4 transition-colors ${theme.page}`}>
      {/* ================= HEADER ================= */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-5xl mx-auto text-center mb-16"
      >
        <h1 className="text-4xl md:text-5xl font-extrabold mb-4">
          Contact Us
        </h1>
        <p className={`text-lg ${theme.muted}`}>
          Get in touch with Deonyak Hearse Service. We are available 24/7.
        </p>
      </motion.div>

      {/* ================= CONTENT ================= */}
      <div className="max-w-6xl mx-auto grid gap-10 lg:grid-cols-2">
        {/* ================= INFO CARD ================= */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          className={`rounded-3xl border p-8 shadow-xl ${theme.card}`}
        >
          <h2 className="text-2xl font-bold mb-6">
            Company Information
          </h2>

          <div className="space-y-6 text-sm">
            {/* NAME */}
            <div className="flex items-start gap-4">
              <Building2 className={theme.icon} size={22} />
              <div>
                <p className="font-semibold">Company Name</p>
                <p className={theme.muted}>
                  Deonyak Hearse Service
                </p>
              </div>
            </div>

            {/* PHONE */}
            <div className="flex items-start gap-4">
              <Phone className={theme.icon} size={22} />
              <div>
                <p className="font-semibold">Contact Numbers</p>
                <p className={theme.muted}>
                  0245226992 / 0244378871
                </p>
              </div>
            </div>

            {/* LOCATION */}
            <div className="flex items-start gap-4">
              <MapPin className={theme.icon} size={22} />
              <div>
                <p className="font-semibold">Location</p>
                <p className={theme.muted}>
                  Bakaekyir, Near Goil Filling Station
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-10">
            <a
              href="tel:0245226992"
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-semibold transition"
            >
              Call Now
            </a>
          </div>
        </motion.div>

        {/* ================= MAP ================= */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          className={`rounded-3xl overflow-hidden border shadow-xl ${theme.card}`}
        >
          <iframe
            src="https://www.google.com/maps/embed?pb=!3m2!1sen!2sgh!4v1769281797383!5m2!1sen!2sgh!6m8!1m7!1sGwg2G14nnYdmMhc0HUDEZQ!2m2!1d4.936000139967281!2d-1.724525359481888!3f343.59406!4f0!5f0.7820865974627469"
            width="100%"
            height="100%"
            style={{ border: 0, minHeight: "420px" }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Deonyak Hearse Service Location"
          />
        </motion.div>
      </div>
    </div>
  );
}
